import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDebounce } from './useDebounce';
import {
  MAX_RANGE_MS,
  MIN_RANGE_MS,
  RANGE_PRESETS,
  TIMEZONE_STORAGE_KEY,
  estimatePointCount,
  quantizeRange,
  resolveResolution,
  type TimeZoneMode,
} from '../utils/starchart';
import type {
  NormalizationMode,
  PopularityWindow,
  StarchartMetric,
  StarchartResolution,
  StarchartServedResolution,
} from '../types/starchart';

const DEFAULT_RANGE_ID = '24h';
const DEFAULT_RANGE_MS =
  RANGE_PRESETS.find((p) => p.id === DEFAULT_RANGE_ID)?.ms ?? 24 * 3600_000;

/** Dates change as the user types; discrete controls do not need this. */
const DATE_DEBOUNCE_MS = 300;

const readStoredTimeZone = (): TimeZoneMode => {
  try {
    return localStorage.getItem(TIMEZONE_STORAGE_KEY) === 'utc' ? 'utc' : 'local';
  } catch {
    // Private browsing and blocked storage both throw here; local is the right default anyway.
    return 'local';
  }
};

export interface StarchartRangeState {
  /** Absolute bounds, unclamped, as chosen by the user. */
  start: Date;
  end: Date;
  /** Bounds floored to the resolution bucket — what the service should actually request. */
  fetchStart: Date;
  fetchEnd: Date;
  spanMs: number;
  /** The preset currently matched, or 'custom'. */
  presetId: string;
  resolutionPreference: StarchartResolution;
  /** The resolution that will actually be requested. */
  resolution: StarchartServedResolution;
  estimatedPoints: number;
  metric: StarchartMetric;
  popularityWindow: PopularityWindow;
  normalization: NormalizationMode;
  movingAverageWindow: number;
  timeZone: TimeZoneMode;
  /** Servers overlaid on the chart, in the API's own casing. */
  overlayIds: string[];
  /** Whether the network total is overlaid alongside them. */
  networkOverlay: boolean;
  /** True while a brush has narrowed the range away from its preset. */
  isZoomed: boolean;
  setPreset: (presetId: string) => void;
  setStart: (date: Date) => void;
  setEnd: (date: Date) => void;
  setResolution: (resolution: StarchartResolution) => void;
  setMetric: (metric: StarchartMetric) => void;
  setPopularityWindow: (window: PopularityWindow) => void;
  setNormalization: (mode: NormalizationMode) => void;
  setMovingAverageWindow: (size: number) => void;
  setTimeZone: (mode: TimeZoneMode) => void;
  toggleOverlay: (serverId: string) => void;
  setNetworkOverlay: (enabled: boolean) => void;
  clearOverlays: () => void;
  /** Commit a brush selection from the chart. */
  zoomTo: (startMs: number, endMs: number) => void;
  resetZoom: () => void;
}

/**
 * Range, metric and display state for a StarChart view, synced to the URL.
 *
 * Living in the query string is what makes a chart shareable — "look at this spike" becomes a
 * link rather than a screenshot plus instructions. The standalone site held all of this in
 * component state, so every view was ephemeral.
 */
export function useStarchartRange(): StarchartRangeState {
  const [params, setParams] = useSearchParams();
  const [timeZone, setTimeZoneState] = useState<TimeZoneMode>(readStoredTimeZone);

  const presetId = params.get('range') ?? DEFAULT_RANGE_ID;
  const fromParam = params.get('from');
  const toParam = params.get('to');
  const metric = (params.get('metric') as StarchartMetric | null) ?? 'players';
  const popularityWindow = (params.get('window') as PopularityWindow | null) ?? '28d';
  const normalization = (params.get('norm') as NormalizationMode | null) ?? 'none';
  const resolutionPreference = (params.get('res') as StarchartResolution | null) ?? 'auto';
  const movingAverageWindow = Number(params.get('ma')) || 5;

  // Overlays live in the URL like everything else here: a comparison you cannot send to someone
  // is most of the way to useless, and the standalone site's comparisons were all ephemeral.
  const overlayParam = params.get('overlay');
  const networkOverlay = params.get('net') === '1';

  // An absolute range in the URL wins; otherwise the preset is relative to now. `Math.floor` to
  // the minute keeps "now" from producing a new object identity on every render.
  const nowMinute = Math.floor(Date.now() / 60_000) * 60_000;

  const rawEnd = toParam ? Number(toParam) : nowMinute;
  const rawStart = fromParam
    ? Number(fromParam)
    : rawEnd - (RANGE_PRESETS.find((p) => p.id === presetId)?.ms ?? DEFAULT_RANGE_MS);

  // Debounce only the absolute bounds: typing in a datetime field should not fire a request per
  // keystroke, but switching preset or metric should be immediate.
  const debouncedStart = useDebounce(rawStart, DATE_DEBOUNCE_MS);
  const debouncedEnd = useDebounce(rawEnd, DATE_DEBOUNCE_MS);

  const { start, end, spanMs } = useMemo(() => {
    let s = debouncedStart;
    let e = debouncedEnd;
    if (!isFinite(s) || !isFinite(e) || e <= s) {
      e = nowMinute;
      s = e - DEFAULT_RANGE_MS;
    }
    const span = Math.min(Math.max(e - s, MIN_RANGE_MS), MAX_RANGE_MS);
    return { start: new Date(e - span), end: new Date(e), spanMs: span };
    // `nowMinute` is intentionally excluded: it ticks every minute and would otherwise
    // re-derive the range (and refetch) on a timer even when nothing was touched.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedStart, debouncedEnd]);

  const overlayIds = useMemo(
    () =>
      overlayParam
        ? overlayParam
            .split(',')
            .map((id) => id.trim())
            .filter(Boolean)
        : [],
    [overlayParam],
  );

  const resolution = useMemo(
    () => resolveResolution(spanMs, resolutionPreference),
    [spanMs, resolutionPreference],
  );

  const { fetchStart, fetchEnd } = useMemo(() => {
    const q = quantizeRange(start.getTime(), end.getTime(), resolution);
    return { fetchStart: q.start, fetchEnd: q.end };
  }, [start, end, resolution]);

  const estimatedPoints = useMemo(
    () => estimatePointCount(spanMs, resolution),
    [spanMs, resolution],
  );

  const patch = useCallback(
    (updates: Record<string, string | null>) => {
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [key, value] of Object.entries(updates)) {
            if (value === null) next.delete(key);
            else next.set(key, value);
          }
          return next;
        },
        { replace: true },
      );
    },
    [setParams],
  );

  const setPreset = useCallback(
    (id: string) => patch({ range: id, from: null, to: null }),
    [patch],
  );

  const setStart = useCallback(
    (date: Date) => patch({ range: 'custom', from: String(date.getTime()), to: String(end.getTime()) }),
    [patch, end],
  );

  const setEnd = useCallback(
    (date: Date) => patch({ range: 'custom', from: String(start.getTime()), to: String(date.getTime()) }),
    [patch, start],
  );

  const zoomTo = useCallback(
    (startMs: number, endMs: number) => {
      if (endMs - startMs < MIN_RANGE_MS) return;
      patch({ range: 'custom', from: String(Math.round(startMs)), to: String(Math.round(endMs)) });
    },
    [patch],
  );

  const resetZoom = useCallback(() => setPreset(DEFAULT_RANGE_ID), [setPreset]);

  const toggleOverlay = useCallback(
    (serverId: string) => {
      const next = overlayIds.includes(serverId)
        ? overlayIds.filter((id) => id !== serverId)
        : [...overlayIds, serverId];
      patch({ overlay: next.length > 0 ? next.join(',') : null });
    },
    [overlayIds, patch],
  );

  const setNetworkOverlay = useCallback(
    (enabled: boolean) => patch({ net: enabled ? '1' : null }),
    [patch],
  );

  const clearOverlays = useCallback(() => patch({ overlay: null, net: null }), [patch]);

  const setTimeZone = useCallback((mode: TimeZoneMode) => {
    setTimeZoneState(mode);
    try {
      localStorage.setItem(TIMEZONE_STORAGE_KEY, mode);
    } catch {
      // A blocked storage write is not worth failing the interaction over.
    }
  }, []);

  // Popularity has no meaning without a window; keep the pair coherent in the URL.
  useEffect(() => {
    if (metric !== 'popularity' && params.get('window')) {
      patch({ window: null });
    }
  }, [metric, params, patch]);

  return {
    start,
    end,
    fetchStart,
    fetchEnd,
    spanMs,
    presetId: fromParam || toParam ? 'custom' : presetId,
    resolutionPreference,
    resolution,
    estimatedPoints,
    metric,
    popularityWindow,
    normalization,
    movingAverageWindow,
    timeZone,
    overlayIds,
    networkOverlay,
    isZoomed: Boolean(fromParam || toParam),
    setPreset,
    setStart,
    setEnd,
    setResolution: (value) => patch({ res: value === 'auto' ? null : value }),
    setMetric: (value) => patch({ metric: value === 'players' ? null : value }),
    setPopularityWindow: (value) => patch({ window: value }),
    setNormalization: (value) => patch({ norm: value === 'none' ? null : value }),
    setMovingAverageWindow: (size) => patch({ ma: String(size) }),
    setTimeZone,
    toggleOverlay,
    setNetworkOverlay,
    clearOverlays,
    zoomTo,
    resetZoom,
  };
}
